import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Table, Alert, Badge } from 'react-bootstrap';
import { Download, Calendar, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import { getLeaveStatement, getLeaveTransactions, getMonthlyLeaveReport } from '../../components/api/LeaveApi';
import { getEmployeesWithLeaveApplicable } from '../../components/api/EmpMasterApi';
import MonthlyLeaveChart from '../../components/leave/MonthlyLeaveChart';

const LeaveStatement = () => {
     const [statement, setStatement] = useState([]);
     const [transactions, setTransactions] = useState([]);
     const [monthlyData, setMonthlyData] = useState([]);
     const [loading, setLoading] = useState(false);
     const [error, setError] = useState('');
     const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
     const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
     const [viewType, setViewType] = useState('statement');
     const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
     const [dateRange, setDateRange] = useState({
          startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0]
     });

     const userRole = sessionStorage.getItem('user_role');
     const isHROrAdmin = ['HR', 'SUPER_ADMIN', 'admin'].includes(userRole);
     const loggedInEmpCode = sessionStorage.getItem('user_code');

     const [selectedEmpCode, setSelectedEmpCode] = useState('');
     const [employees, setEmployees] = useState([]);

     useEffect(() => {
          const handleResize = () => setIsMobile(window.innerWidth < 768);
          window.addEventListener('resize', handleResize);
          return () => window.removeEventListener('resize', handleResize);
     }, []);

     useEffect(() => {
          const initializeComponent = async () => {
               if (!isHROrAdmin) {
                    setSelectedEmpCode(loggedInEmpCode);
               } else {
                    await fetchEmployees();
               }
          };
          initializeComponent();
     }, []);

     useEffect(() => {
          if (selectedEmpCode && selectedEmpCode !== '' && selectedEmpCode !== 'null') {
               fetchData();
          } else {
               setStatement([]);
               setTransactions([]);
               setMonthlyData([]);
               setError('');
          }
     }, [selectedEmpCode, selectedYear, selectedMonth, viewType, dateRange.startDate, dateRange.endDate]);

     const fetchEmployees = async () => {
          try {
               const response = await getEmployeesWithLeaveApplicable();
               if (response && response.data && response.data.length > 0) {
                    setEmployees(response.data);
               } else {
                    setEmployees([]);
               }
          } catch (err) {
               console.error('Error fetching employees:', err);
               setEmployees([]);
          }
     };

     const fetchData = async () => {
          try {
               setLoading(true);
               setError('');

               const empCode = selectedEmpCode;
               if (!empCode || empCode === 'null') {
                    setError('Employee code not found. Please login again.');
                    setLoading(false);
                    return;
               }

               if (viewType === 'statement') {
                    const financialYear = `${selectedYear}-${selectedYear + 1}`;
                    const response = await getLeaveStatement(empCode, financialYear);

                    let statementData = [];
                    if (Array.isArray(response)) {
                         statementData = response;
                    } else if (response && response.data) {
                         statementData = Array.isArray(response.data) ? response.data : [];
                    } else if (response && response.statement) {
                         statementData = Array.isArray(response.statement) ? response.statement : [];
                    }

                    setStatement(statementData);
                    if (statementData.length === 0) {
                         setError('No leave balance records found for this employee. Please contact HR to credit leaves.');
                    }
               } else if (viewType === 'transactions') {
                    const response = await getLeaveTransactions(empCode, dateRange.startDate, dateRange.endDate);
                    const txnData = Array.isArray(response) ? response : (response.transactions || response.data || []);
                    setTransactions(txnData);
                    if (txnData.length === 0) {
                         setError('No transactions found for the selected period.');
                    }
               } else if (viewType === 'monthly') {
                    const response = await getMonthlyLeaveReport(empCode, selectedYear, selectedMonth);
                    const monthlyReportData = Array.isArray(response) ? response : (response.report || response.data || []);
                    setMonthlyData(monthlyReportData);
                    if (monthlyReportData.length === 0) {
                         setError('No leave records found for the selected month.');
                    }
               }
          } catch (err) {
               console.error('Fetch error:', err);
               setError('Failed to fetch leave data: ' + (err.message || 'Unknown error'));
               setStatement([]);
               setTransactions([]);
               setMonthlyData([]);
          } finally {
               setLoading(false);
          }
     };

     const getTransactionTypeColor = (type) => {
          const colors = { 'CREDIT': 'success', 'DEBIT': 'danger', 'ADJUSTMENT': 'warning' };
          return colors[type] || 'secondary';
     };

     const exportToCSV = () => {
          let csvContent = '';
          let filename = '';

          if (viewType === 'statement') {
               csvContent = 'Leave Type,Opening Balance,Credited,Consumed,Closing Balance\n';
               statement.forEach(row => {
                    csvContent += `${row.leave_type},${row.opening_balance || 0},${row.total_credited || row.total_leaves || 0},${row.used_leaves || row.total_consumed || 0},${row.remaining_leaves || row.closing_balance || 0}\n`;
               });
               filename = `leave_statement_${selectedEmpCode}_${selectedYear}.csv`;
          } else if (viewType === 'transactions') {
               csvContent = 'Date,Type,Leave Type,Days,Balance After,Remarks\n';
               transactions.forEach(txn => {
                    csvContent += `${new Date(txn.transaction_date).toLocaleDateString()},${txn.transaction_type},${txn.leave_type},${txn.days},${txn.balance_after},${txn.remarks || ''}\n`;
               });
               filename = `leave_transactions_${selectedEmpCode}_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
          } else {
               csvContent = 'Date,Leave Type,Days,Status,Remarks\n';
               monthlyData.forEach(row => {
                    csvContent += `${new Date(row.date).toLocaleDateString()},${row.leave_type},${row.days},${row.status},${row.remarks || ''}\n`;
               });
               filename = `leave_report_${selectedEmpCode}_${selectedYear}_${selectedMonth}.csv`;
          }

          const blob = new Blob([csvContent], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
     };

     const calculateTotals = () => {
          if (viewType !== 'statement' || statement.length === 0) return null;
          return statement.reduce((acc, row) => ({
               opening: acc.opening + parseFloat(row.opening_balance || 0),
               credited: acc.credited + parseFloat(row.total_credited || row.total_leaves || 0),
               consumed: acc.consumed + parseFloat(row.used_leaves || row.total_consumed || 0),
               closing: acc.closing + parseFloat(row.remaining_leaves || row.closing_balance || 0)
          }), { opening: 0, credited: 0, consumed: 0, closing: 0 });
     };

     const months = [
          { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
          { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
          { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
          { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
     ];

     return (
          <div className="leave-statement-section" style={{ padding: isMobile ? '12px' : '24px' }}>
               {/* Header */}
               <Row className="mb-3">
                    <Col xs={12} md={3} className="mb-2 mb-md-0">
                         <h4 style={{ fontSize: isMobile ? '18px' : '24px' }}>Leave Statement</h4>
                    </Col>
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                         <div className={`d-flex gap-${isMobile ? '1' : '2'} ${isMobile ? 'flex-column' : 'flex-row'}`}>
                              <Button
                                   variant={viewType === 'statement' ? 'primary' : 'outline-primary'}
                                   size="sm"
                                   onClick={() => setViewType('statement')}
                                   className={isMobile ? 'w-100' : 'flex-fill'}
                                   style={{ fontSize: isMobile ? '12px' : '13px', padding: isMobile ? '6px 8px' : '6px 12px' }}
                              >
                                   <FileText size={14} className="me-1" />
                                   Statement
                              </Button>
                              <Button
                                   variant={viewType === 'transactions' ? 'primary' : 'outline-primary'}
                                   size="sm"
                                   onClick={() => setViewType('transactions')}
                                   className={isMobile ? 'w-100' : 'flex-fill'}
                                   style={{ fontSize: isMobile ? '12px' : '13px', padding: isMobile ? '6px 8px' : '6px 12px' }}
                              >
                                   <Calendar size={14} className="me-1" />
                                   Transactions
                              </Button>
                              <Button
                                   variant={viewType === 'monthly' ? 'primary' : 'outline-primary'}
                                   size="sm"
                                   onClick={() => setViewType('monthly')}
                                   className={isMobile ? 'w-100' : 'flex-fill'}
                                   style={{ fontSize: isMobile ? '12px' : '13px', padding: isMobile ? '6px 8px' : '6px 12px' }}
                              >
                                   <TrendingUp size={14} className="me-1" />
                                   {isMobile ? 'Monthly' : 'Monthly Report'}
                              </Button>
                         </div>
                    </Col>
                    <Col xs={12} md={3} className={isMobile ? '' : 'text-end'}>
                         <Button
                              variant="success"
                              onClick={exportToCSV}
                              size="sm"
                              disabled={loading || statement.length === 0}
                              className={isMobile ? 'w-100' : ''}
                              style={{ fontSize: isMobile ? '12px' : '13px' }}
                         >
                              <Download size={14} className="me-1" />
                              Export CSV
                         </Button>
                    </Col>
               </Row>

               {/* Filters */}
               <Row className="mb-3">
                    <Col xs={12}>
                         <Card style={{ borderRadius: isMobile ? '8px' : '12px' }}>
                              <Card.Body style={{ padding: isMobile ? '12px' : '16px' }}>
                                   <Row className="g-2">
                                        {/* Employee Selector */}
                                        {isHROrAdmin && (
                                             <Col xs={12} md={viewType === 'statement' ? 6 : viewType === 'transactions' ? 4 : 4}>
                                                  <Form.Group>
                                                       <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                            Employee
                                                       </Form.Label>
                                                       <Form.Select
                                                            value={selectedEmpCode}
                                                            onChange={(e) => setSelectedEmpCode(e.target.value)}
                                                            size="sm"
                                                            style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                       >
                                                            <option value="">Select Employee</option>
                                                            {employees.map(emp => (
                                                                 <option key={emp.employee_code} value={emp.employee_code}>
                                                                      {emp.employee_code} - {emp.employee_name}
                                                                 </option>
                                                            ))}
                                                       </Form.Select>
                                                  </Form.Group>
                                             </Col>
                                        )}

                                        {/* Statement View Filters */}
                                        {viewType === 'statement' && (
                                             <Col xs={12} md={isHROrAdmin ? 6 : 12}>
                                                  <Form.Group>
                                                       <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                            Financial Year
                                                       </Form.Label>
                                                       <Form.Select
                                                            value={selectedYear}
                                                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                                            size="sm"
                                                            style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                       >
                                                            {[...Array(5)].map((_, i) => {
                                                                 const year = new Date().getFullYear() - 2 + i;
                                                                 return <option key={year} value={year}>{year}-{year + 1}</option>;
                                                            })}
                                                       </Form.Select>
                                                  </Form.Group>
                                             </Col>
                                        )}

                                        {/* Transactions View Filters */}
                                        {viewType === 'transactions' && (
                                             <>
                                                  <Col xs={12} md={4}>
                                                       <Form.Group>
                                                            <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                                 From Date
                                                            </Form.Label>
                                                            <Form.Control
                                                                 type="date"
                                                                 value={dateRange.startDate}
                                                                 onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                                                                 size="sm"
                                                                 style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                            />
                                                       </Form.Group>
                                                  </Col>
                                                  <Col xs={12} md={4}>
                                                       <Form.Group>
                                                            <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                                 To Date
                                                            </Form.Label>
                                                            <Form.Control
                                                                 type="date"
                                                                 value={dateRange.endDate}
                                                                 onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                                                                 size="sm"
                                                                 style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                            />
                                                       </Form.Group>
                                                  </Col>
                                             </>
                                        )}

                                        {/* Monthly View Filters */}
                                        {viewType === 'monthly' && (
                                             <>
                                                  <Col xs={12} md={4}>
                                                       <Form.Group>
                                                            <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                                 Year
                                                            </Form.Label>
                                                            <Form.Select
                                                                 value={selectedYear}
                                                                 onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                                                 size="sm"
                                                                 style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                            >
                                                                 {[...Array(5)].map((_, i) => {
                                                                      const year = new Date().getFullYear() - 2 + i;
                                                                      return <option key={year} value={year}>{year}</option>;
                                                                 })}
                                                            </Form.Select>
                                                       </Form.Group>
                                                  </Col>
                                                  <Col xs={12} md={4}>
                                                       <Form.Group>
                                                            <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                                 Month
                                                            </Form.Label>
                                                            <Form.Select
                                                                 value={selectedMonth}
                                                                 onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                                                 size="sm"
                                                                 style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                            >
                                                                 {months.map(month => (
                                                                      <option key={month.value} value={month.value}>{month.label}</option>
                                                                 ))}
                                                            </Form.Select>
                                                       </Form.Group>
                                                  </Col>
                                             </>
                                        )}
                                   </Row>
                              </Card.Body>
                         </Card>
                    </Col>
               </Row>

               {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')} style={{ fontSize: isMobile ? '13px' : '14px' }}>
                         {error}
                    </Alert>
               )}

               {/* Statement View */}
               {viewType === 'statement' && (
                    selectedEmpCode ? (
                         <Card style={{ borderRadius: isMobile ? '8px' : '12px' }}>
                              <Card.Header className="bg-light" style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
                                   <h5 className="mb-0" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                        Annual Leave Statement - FY {selectedYear}-{selectedYear + 1}
                                   </h5>
                              </Card.Header>
                              <Card.Body className="p-0">
                                   {loading ? (
                                        <div className="text-center py-5">
                                             <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                  <span className="visually-hidden">Loading...</span>
                                             </div>
                                        </div>
                                   ) : statement.length === 0 ? (
                                        <div className="text-center py-5">
                                             <FileText size={isMobile ? 48 : 64} className="text-muted mb-3" />
                                             <h6 className="text-muted" style={{ fontSize: isMobile ? '14px' : '16px' }}>No statement data available</h6>
                                             <p className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Please contact HR to credit leaves.</p>
                                        </div>
                                   ) : isMobile ? (
                                        // Mobile Card View
                                        <div style={{ padding: '12px' }}>
                                             {statement.map((row, index) => (
                                                  <Card key={index} className="mb-2" style={{ fontSize: '13px' }}>
                                                       <Card.Body style={{ padding: '12px' }}>
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                 <div>
                                                                      <Badge bg={row.leave_type === 'PL' ? 'primary' : row.leave_type === 'CL' ? 'success' : 'warning'} style={{ fontSize: '11px' }}>
                                                                           {row.leave_type}
                                                                      </Badge>
                                                                      <span className="ms-2 small">
                                                                           {row.leave_type === 'PL' ? 'Privilege Leave' : row.leave_type === 'CL' ? 'Casual Leave' : 'Sick Leave'}
                                                                      </span>
                                                                 </div>
                                                            </div>
                                                            <Row className="g-2">
                                                                 <Col xs={6}>
                                                                      <small className="text-muted d-block" style={{ fontSize: '11px' }}>Opening</small>
                                                                      <strong style={{ fontSize: '14px' }}>{parseFloat(row.opening_balance || 0).toFixed(1)}</strong>
                                                                 </Col>
                                                                 <Col xs={6}>
                                                                      <small className="text-muted d-block" style={{ fontSize: '11px' }}>Credited</small>
                                                                      <strong className="text-success" style={{ fontSize: '14px' }}>{parseFloat(row.total_credited || row.total_leaves || 0).toFixed(1)}</strong>
                                                                 </Col>
                                                                 <Col xs={6}>
                                                                      <small className="text-muted d-block" style={{ fontSize: '11px' }}>Consumed</small>
                                                                      <strong className="text-danger" style={{ fontSize: '14px' }}>{parseFloat(row.used_leaves || row.total_consumed || 0).toFixed(1)}</strong>
                                                                 </Col>
                                                                 <Col xs={6}>
                                                                      <small className="text-muted d-block" style={{ fontSize: '11px' }}>Closing</small>
                                                                      <strong className="text-primary" style={{ fontSize: '14px' }}>{parseFloat(row.remaining_leaves || row.closing_balance || 0).toFixed(1)}</strong>
                                                                 </Col>
                                                            </Row>
                                                       </Card.Body>
                                                  </Card>
                                             ))}
                                        </div>
                                   ) : (
                                        // Desktop Table View
                                        <div className="table-responsive">
                                             <Table className="mb-0" style={{ fontSize: '14px' }}>
                                                  <thead className="table-light">
                                                       <tr>
                                                            <th>Leave Type</th>
                                                            <th className="text-end">Opening Balance</th>
                                                            <th className="text-end">Credited</th>
                                                            <th className="text-end">Consumed</th>
                                                            <th className="text-end">Closing Balance</th>
                                                       </tr>
                                                  </thead>
                                                  <tbody>
                                                       {statement.map((row, index) => (
                                                            <tr key={index}>
                                                                 <td>
                                                                      <Badge bg={row.leave_type === 'PL' ? 'primary' : row.leave_type === 'CL' ? 'success' : 'warning'}>
                                                                           {row.leave_type}
                                                                      </Badge>
                                                                      <span className="ms-2">
                                                                           {row.leave_type === 'PL' ? 'Privilege Leave' : row.leave_type === 'CL' ? 'Casual Leave' : 'Sick Leave'}
                                                                      </span>
                                                                 </td>
                                                                 <td className="text-end">{parseFloat(row.opening_balance || 0).toFixed(1)}</td>
                                                                 <td className="text-end text-success">
                                                                      <TrendingUp size={14} className="me-1" />
                                                                      {parseFloat(row.total_credited || row.total_leaves || 0).toFixed(1)}
                                                                 </td>
                                                                 <td className="text-end text-danger">
                                                                      <TrendingDown size={14} className="me-1" />
                                                                      {parseFloat(row.used_leaves || row.total_consumed || 0).toFixed(1)}
                                                                 </td>
                                                                 <td className="text-end">
                                                                      <strong>{parseFloat(row.remaining_leaves || row.closing_balance || 0).toFixed(1)}</strong>
                                                                 </td>
                                                            </tr>
                                                       ))}
                                                  </tbody>
                                                  {statement.length > 0 && calculateTotals() && (
                                                       <tfoot className="table-light">
                                                            <tr>
                                                                 <th>Total</th>
                                                                 <th className="text-end">{calculateTotals().opening.toFixed(1)}</th>
                                                                 <th className="text-end text-success">{calculateTotals().credited.toFixed(1)}</th>
                                                                 <th className="text-end text-danger">{calculateTotals().consumed.toFixed(1)}</th>
                                                                 <th className="text-end"><strong>{calculateTotals().closing.toFixed(1)}</strong></th>
                                                            </tr>
                                                       </tfoot>
                                                  )}
                                             </Table>
                                        </div>
                                   )}
                              </Card.Body>
                         </Card>
                    ) : (
                         <Card>
                              <Card.Body className="text-center py-5">
                                   <FileText size={isMobile ? 48 : 64} className="text-muted mb-3" />
                                   <h6 className="text-muted" style={{ fontSize: isMobile ? '14px' : '16px' }}>Select an Employee</h6>
                                   <p className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Please select an employee to view their statement.</p>
                              </Card.Body>
                         </Card>
                    )
               )}

               {/* Transactions View */}
               {viewType === 'transactions' && (
                    selectedEmpCode ? (
                         <Card style={{ borderRadius: isMobile ? '8px' : '12px' }}>
                              <Card.Header className="bg-light" style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
                                   <h5 className="mb-0" style={{ fontSize: isMobile ? '14px' : '16px' }}>Leave Transactions</h5>
                              </Card.Header>
                              <Card.Body className="p-0">
                                   {loading ? (
                                        <div className="text-center py-5">
                                             <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                  <span className="visually-hidden">Loading...</span>
                                             </div>
                                        </div>
                                   ) : transactions.length === 0 ? (
                                        <div className="text-center py-5">
                                             <Calendar size={isMobile ? 48 : 64} className="text-muted mb-3" />
                                             <h6 className="text-muted" style={{ fontSize: isMobile ? '14px' : '16px' }}>No transactions found</h6>
                                        </div>
                                   ) : isMobile ? (
                                        // Mobile Card View
                                        <div style={{ padding: '12px' }}>
                                             {transactions.map((txn, index) => (
                                                  <Card key={index} className="mb-2" style={{ fontSize: '13px' }}>
                                                       <Card.Body style={{ padding: '12px' }}>
                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                 <div>
                                                                      <strong style={{ fontSize: '13px' }}>{new Date(txn.transaction_date).toLocaleDateString()}</strong>
                                                                      <div className="mt-1">
                                                                           <Badge bg={getTransactionTypeColor(txn.transaction_type)} style={{ fontSize: '10px', marginRight: '4px' }}>
                                                                                {txn.transaction_type}
                                                                           </Badge>
                                                                           <Badge bg={txn.leave_type === 'PL' ? 'primary' : txn.leave_type === 'CL' ? 'success' : 'warning'} style={{ fontSize: '10px' }}>
                                                                                {txn.leave_type}
                                                                           </Badge>
                                                                      </div>
                                                                 </div>
                                                                 <div className="text-end">
                                                                      <div className={`fw-bold ${txn.transaction_type === 'CREDIT' ? 'text-success' : 'text-danger'}`} style={{ fontSize: '16px' }}>
                                                                           {txn.transaction_type === 'CREDIT' ? '+' : '-'}{txn.days}
                                                                      </div>
                                                                      <small className="text-muted" style={{ fontSize: '11px' }}>Bal: {txn.balance_after}</small>
                                                                 </div>
                                                            </div>
                                                            {txn.remarks && (
                                                                 <div className="mt-2 pt-2 border-top">
                                                                      <small className="text-muted" style={{ fontSize: '11px' }}>{txn.remarks}</small>
                                                                 </div>
                                                            )}
                                                       </Card.Body>
                                                  </Card>
                                             ))}
                                        </div>
                                   ) : (
                                        // Desktop Table View
                                        <div className="table-responsive">
                                             <Table hover className="mb-0" style={{ fontSize: '14px' }}>
                                                  <thead className="table-light">
                                                       <tr>
                                                            <th>Date</th>
                                                            <th>Transaction Type</th>
                                                            <th>Leave Type</th>
                                                            <th className="text-end">Days</th>
                                                            <th className="text-end">Balance After</th>
                                                            <th>Remarks</th>
                                                       </tr>
                                                  </thead>
                                                  <tbody>
                                                       {transactions.map((txn, index) => (
                                                            <tr key={index}>
                                                                 <td>{new Date(txn.transaction_date).toLocaleDateString()}</td>
                                                                 <td>
                                                                      <Badge bg={getTransactionTypeColor(txn.transaction_type)}>{txn.transaction_type}</Badge>
                                                                 </td>
                                                                 <td>
                                                                      <Badge bg={txn.leave_type === 'PL' ? 'primary' : txn.leave_type === 'CL' ? 'success' : 'warning'}>
                                                                           {txn.leave_type}
                                                                      </Badge>
                                                                 </td>
                                                                 <td className={`text-end ${txn.transaction_type === 'CREDIT' ? 'text-success' : 'text-danger'}`}>
                                                                      {txn.transaction_type === 'CREDIT' ? '+' : '-'}{txn.days}
                                                                 </td>
                                                                 <td className="text-end"><strong>{txn.balance_after}</strong></td>
                                                                 <td><small className="text-muted">{txn.remarks || '-'}</small></td>
                                                            </tr>
                                                       ))}
                                                  </tbody>
                                             </Table>
                                        </div>
                                   )}
                              </Card.Body>
                         </Card>
                    ) : (
                         <Card>
                              <Card.Body className="text-center py-5">
                                   <Calendar size={isMobile ? 48 : 64} className="text-muted mb-3" />
                                   <h6 className="text-muted" style={{ fontSize: isMobile ? '14px' : '16px' }}>Select an Employee</h6>
                                   <p className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Please select an employee to view their transactions.</p>
                              </Card.Body>
                         </Card>
                    )
               )}

               {/* Monthly Report View */}
               {viewType === 'monthly' && (
                    selectedEmpCode ? (
                         <>
                              <Row className="mb-3">
                                   <Col md={12}>
                                        <Card style={{ borderRadius: isMobile ? '8px' : '12px' }}>
                                             <Card.Header className="bg-light" style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
                                                  <h5 className="mb-0" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                                       Monthly Leave Chart - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
                                                  </h5>
                                             </Card.Header>
                                             <Card.Body style={{ padding: isMobile ? '12px' : '16px' }}>
                                                  {loading ? (
                                                       <div className="text-center py-5">
                                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                                 <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                       </div>
                                                  ) : monthlyData.length === 0 ? (
                                                       <div className="text-center py-5">
                                                            <TrendingUp size={isMobile ? 48 : 64} className="text-muted mb-3" />
                                                            <h6 className="text-muted" style={{ fontSize: isMobile ? '14px' : '16px' }}>No leave records for this month</h6>
                                                       </div>
                                                  ) : (
                                                       <MonthlyLeaveChart data={monthlyData} />
                                                  )}
                                             </Card.Body>
                                        </Card>
                                   </Col>
                              </Row>

                              {!loading && monthlyData.length > 0 && (
                                   <Card style={{ borderRadius: isMobile ? '8px' : '12px' }}>
                                        <Card.Header className="bg-light" style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
                                             <h5 className="mb-0" style={{ fontSize: isMobile ? '14px' : '16px' }}>Leave Details</h5>
                                        </Card.Header>
                                        <Card.Body className="p-0">
                                             {isMobile ? (
                                                  // Mobile Card View
                                                  <div style={{ padding: '12px' }}>
                                                       {monthlyData.map((row, index) => {
                                                            const date = new Date(row.date);
                                                            return (
                                                                 <Card key={index} className="mb-2" style={{ fontSize: '13px' }}>
                                                                      <Card.Body style={{ padding: '12px' }}>
                                                                           <div className="d-flex justify-content-between align-items-start mb-2">
                                                                                <div>
                                                                                     <strong style={{ fontSize: '13px' }}>{date.toLocaleDateString()}</strong>
                                                                                     <div className="text-muted small">{date.toLocaleDateString('en-US', { weekday: 'long' })}</div>
                                                                                </div>
                                                                                {row.leave_type && (
                                                                                     <Badge bg={row.leave_type === 'PL' ? 'primary' : row.leave_type === 'CL' ? 'success' : 'warning'} style={{ fontSize: '10px' }}>
                                                                                          {row.leave_type}
                                                                                     </Badge>
                                                                                )}
                                                                           </div>
                                                                           <Row className="g-2">
                                                                                <Col xs={6}>
                                                                                     <small className="text-muted d-block" style={{ fontSize: '11px' }}>Days</small>
                                                                                     <strong>{row.days || '-'}</strong>
                                                                                </Col>
                                                                                <Col xs={6}>
                                                                                     <small className="text-muted d-block" style={{ fontSize: '11px' }}>Status</small>
                                                                                     {row.status && (
                                                                                          <Badge bg={row.status === 'approved' ? 'success' : 'warning'} style={{ fontSize: '10px' }}>
                                                                                               {row.status}
                                                                                          </Badge>
                                                                                     )}
                                                                                </Col>
                                                                           </Row>
                                                                           {row.remarks && (
                                                                                <div className="mt-2 pt-2 border-top">
                                                                                     <small className="text-muted" style={{ fontSize: '11px' }}>{row.remarks}</small>
                                                                                </div>
                                                                           )}
                                                                      </Card.Body>
                                                                 </Card>
                                                            );
                                                       })}
                                                  </div>
                                             ) : (
                                                  // Desktop Table View
                                                  <div className="table-responsive">
                                                       <Table hover className="mb-0" style={{ fontSize: '14px' }}>
                                                            <thead className="table-light">
                                                                 <tr>
                                                                      <th>Date</th>
                                                                      <th>Day</th>
                                                                      <th>Leave Type</th>
                                                                      <th className="text-end">Days</th>
                                                                      <th>Status</th>
                                                                      <th>Remarks</th>
                                                                 </tr>
                                                            </thead>
                                                            <tbody>
                                                                 {monthlyData.map((row, index) => {
                                                                      const date = new Date(row.date);
                                                                      return (
                                                                           <tr key={index}>
                                                                                <td>{date.toLocaleDateString()}</td>
                                                                                <td>{date.toLocaleDateString('en-US', { weekday: 'short' })}</td>
                                                                                <td>
                                                                                     {row.leave_type && (
                                                                                          <Badge bg={row.leave_type === 'PL' ? 'primary' : row.leave_type === 'CL' ? 'success' : 'warning'}>
                                                                                               {row.leave_type}
                                                                                          </Badge>
                                                                                     )}
                                                                                </td>
                                                                                <td className="text-end">{row.days || '-'}</td>
                                                                                <td>
                                                                                     {row.status && (
                                                                                          <Badge bg={row.status === 'approved' ? 'success' : 'warning'}>
                                                                                               {row.status}
                                                                                          </Badge>
                                                                                     )}
                                                                                </td>
                                                                                <td><small className="text-muted">{row.remarks || '-'}</small></td>
                                                                           </tr>
                                                                      );
                                                                 })}
                                                            </tbody>
                                                       </Table>
                                                  </div>
                                             )}
                                        </Card.Body>
                                   </Card>
                              )}
                         </>
                    ) : (
                         <Card>
                              <Card.Body className="text-center py-5">
                                   <TrendingUp size={isMobile ? 48 : 64} className="text-muted mb-3" />
                                   <h6 className="text-muted" style={{ fontSize: isMobile ? '14px' : '16px' }}>Select an Employee</h6>
                                   <p className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Please select an employee to view their monthly report.</p>
                              </Card.Body>
                         </Card>
                    )
               )}
          </div>
     );
};

export default LeaveStatement;